import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPanel,
  AlertDialogPopup,
  AlertDialogTitle,
  Button,
} from "@salestrak/ui";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import {
  TypedConfirmationField,
  confirmationMatches,
} from "./typed-confirmation-field";

interface IssueTypeDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueTypeName: string;
  /** Live projects wearing this type — the delete converts them to the
   *  workspace default type, and the copy states how many rows that touches. */
  issueCount: number;
  onConfirm: () => Promise<void>;
}

export function deleteDescription(
  issueCount: number,
  defaultTypeName: string | null,
): string {
  const target = defaultTypeName ? `the "${defaultTypeName}" type` : "the default type";
  const conversion =
    issueCount === 0
      ? "No existing Projects use this type."
      : `${issueCount.toLocaleString()} ${issueCount === 1 ? "Project" : "Projects"} using this type will change to ${target}.`;
  return `This Project type will be permanently deleted. ${conversion}`;
}

export function IssueTypeDeleteDialog({
  open,
  onOpenChange,
  issueTypeName,
  issueCount,
  onConfirm,
}: IssueTypeDeleteDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  // The delete moves every Project of this type to the workspace default.
  const { issueTypes } = useWorkspaceData();
  const defaultTypeName = issueTypes.find((t) => t.isDefault)?.name ?? null;
  // A type in use asks for its name, the way a workflow delete does: one
  // click must not retype thousands of Projects.
  const needsTypedName = issueCount > 0;
  const confirmed = !needsTypedName || confirmationMatches(confirmName, issueTypeName);

  useEffect(() => {
    if (!open) setConfirmName("");
  }, [open]);

  async function handleDelete() {
    if (!confirmed) return;
    setDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setDeleting(false);
    }
  }

  // One AlertDialog surface at every breakpoint.
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete &ldquo;{issueTypeName}&rdquo;?</AlertDialogTitle>
          <AlertDialogDescription>
            {deleteDescription(issueCount, defaultTypeName)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {needsTypedName ? (
          <AlertDialogPanel>
            <TypedConfirmationField
              noun="type"
              name={issueTypeName}
              value={confirmName}
              onChange={setConfirmName}
              onSubmit={() => void handleDelete()}
              disabled={deleting}
            />
          </AlertDialogPanel>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogClose
            render={<Button variant="outline" />}
            disabled={deleting}
          >
            Cancel
          </AlertDialogClose>
          <Button
            color="destructive"
            onClick={handleDelete}
            disabled={deleting || !confirmed}
            loading={deleting}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
